import { bound } from "@frui.ts/helpers";
import type { ScreenBase } from "@frui.ts/screens";
import { ConductorOneChildActive, Router } from "@frui.ts/screens";
import { Modules } from "config";
import { computed } from "mobx";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import EventBus, { blacklistedAccountsChanged } from "../../../services/eventBus";
import type { IModuleViewModel } from "../../types";
import CustomerRefundCandidatesViewModel from "./customerRefundCandidates/customerRefundCandidatesViewModel";
import RejectedCustomerRefundCandidatesViewModel from "./rejectedCustomerRefundCandidates/rejectedCustomerRefundCandidatesViewModel";

const navigationName = "customer-refunds";

@Router.registerRoute({
  route: navigationName,
  name: Router.Self,
  children: [CustomerRefundCandidatesViewModel, RejectedCustomerRefundCandidatesViewModel],
})
export default class CustomerRefundsViewModel extends ConductorOneChildActive<ScreenBase> implements IModuleViewModel {
  showChildrenSubmenu = true;
  private unsubscriptions: Array<() => void> = [];

  @computed
  get name() {
    return this.translate("name");
  }

  constructor(
    private localizationService: LocalizationService,
    private customerRefundCandidatesViewModel: CustomerRefundCandidatesViewModel,
    private rejectedCustomerRefundCandidatesViewModel: RejectedCustomerRefundCandidatesViewModel,
    private securityService: SecurityService,
    protected eventBus: EventBus
  ) {
    super();
    this.navigationName = navigationName;

    if (
      this.securityService.allowTo("finance") &&
      (this.securityService.allowTo("finance.factualauditing") ||
        this.securityService.allowTo("finance.commandoperating") ||
        this.securityService.allowTo("finance.chiefaccounting"))
    ) {
      this.children.push(this.customerRefundCandidatesViewModel);
    }

    if (this.securityService.allowTo("finance") || this.securityService.allowTo("callcenter.finance")) {
      this.children.push(this.rejectedCustomerRefundCandidatesViewModel);
    }
  }

  protected onInitialize(): Promise<any> | void {
    if (!this.activeChild) {
      this.tryActivateChild(this.children[0]);
    }

    this.unsubscriptions.push(this.eventBus.subscribe(blacklistedAccountsChanged, this.onBankAccountBlacklistsChanged));

    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  closeChild(child: ScreenBase) {
    return false;
  }

  @bound
  private onBankAccountBlacklistsChanged(data: ReturnType<typeof blacklistedAccountsChanged>) {
    this.children.forEach(children => {
      // Reload all non empty children lists - we have no data from BE to recognize by account numbers
      if (
        (children instanceof CustomerRefundCandidatesViewModel ||
          children instanceof RejectedCustomerRefundCandidatesViewModel) &&
        children.isInitialized &&
        children.items.length
      ) {
        children.loadData();
      }
    });
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `customerRefunds.${code}`, size);
  }
}
